import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { cities } from "../helpers/config";
import {
  scheduleKey,
  addressKey,
  addressIdKey,
  streetsUrl,
  numbersUrl,
  scheduleURL,
} from "../helpers/config";

type City = {
  id: string | number;
  value: string;
};

type Street = City;
type Address = City;

type SavedSchedule = {
  data: any; // Replace 'any' with the specific type of your schedule data if known
  timestamp: number;
};

const SCHEDULE_EXPIRATION_MS = 30 * 24 * 60 * 60 * 1000; // 30 dni w milisekundach

// TODO add this to API
function removeSpacesFromDates(input: any): any {
  return {
    data: input.data
      .map((entry: any) => ({
        ...entry,
        date: entry.date
          .replace(/\s+/g, "") // Remove all spaces
          .replace(/-(\d)$/, "-0$1"), // Ensure single-digit day is zero-padded
      }))
      .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()), // Sort dates in ascending order
  };
}

function isScheduleValid(savedSchedule: SavedSchedule | null, currentAddressId: string | null): boolean {
  if (!savedSchedule || !savedSchedule.timestamp) return false;
  const { timestamp } = savedSchedule;
  const storedAddressId = localStorage.getItem(addressIdKey);
  return (
    storedAddressId === currentAddressId &&
    Date.now() - timestamp < SCHEDULE_EXPIRATION_MS
  );
}

export function useGetSchedule() {
  const [cityId, setCityId] = useState<string | null>(null);
  const [streets, setStreets] = useState<Street[] | null>(null);
  const [streetId, setStreetId] = useState<string | null>(null);
  const [streetName, setStreetName] = useState<string | null>(null);
  const [addresses, setAddresses] = useState<Address[] | null>(null);
  const [addressId, setAddressId] = useState<string | null>(
    () => localStorage.getItem(addressIdKey) || null
  );
  const [addressLabel, setAddressLabel] = useState<string | null>(null);
  const [fullAddress, setFullAddress] = useState<string | null>(
    () => localStorage.getItem(addressKey) || null
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [schedule, setSchedule] = useState<any>(
    () => {
      const savedSchedule: SavedSchedule | null = JSON.parse(localStorage.getItem(scheduleKey) || "null");
      if (savedSchedule && isScheduleValid(savedSchedule, localStorage.getItem(addressIdKey))) {
        return savedSchedule.data;
      }
      return [];
    }
  );

  const formattedCities = useMemo(
    () =>
      cities.map((item: City) => ({
        value: item.id,
        label: item.value,
      })),
    []
  );

  const formattedStreets = useMemo(
    () =>
      streets?.map((item: Street) => ({
        value: item.id,
        label: item.value,
      })) || null,
    [streets]
  );

  const formattedNumbers = useMemo(
    () =>
      addresses?.map((item: Address) => ({
        value: item.id,
        label: item.value,
      })) || null,
    [addresses]
  );

  const fetchStreets = useCallback(async () => {
    if (!cityId) return;
    try {
      const { data } = await axios.get(`${streetsUrl}/${cityId}`);
      setStreets(data);
    } catch {
      setError(true);
    }
  }, [cityId]);

  const fetchAddresses = useCallback(async () => {
    if (!cityId || !streetId) return;
    try {
      const { data } = await axios.get(`${numbersUrl}/${cityId}/${streetId}`);
      setAddresses(data);
    } catch {
      setError(true);
    }
  }, [cityId, streetId]);

  const fetchSchedule = useCallback(async () => {
    if (!addressId) return;

    const savedSchedule: SavedSchedule | null = JSON.parse(localStorage.getItem(scheduleKey) || "null");

    if (savedSchedule && isScheduleValid(savedSchedule, addressId)) {
      setSchedule(savedSchedule.data);
      return;
    }

    setIsLoading(true);
    try {
      const { data } = await axios.get(scheduleURL, { params: { id: addressId } });
      // TODO: when moved to api fix this part
      setSchedule(removeSpacesFromDates(data).data || []);

      const scheduleToSave: SavedSchedule = {
        data: data.data || [],
        timestamp: Date.now(),
      };

      localStorage.setItem(scheduleKey, JSON.stringify(scheduleToSave));
      localStorage.setItem(addressIdKey, addressId);

      if (streetName && addressLabel) {
        const fullAddr = `ul. ${streetName} ${addressLabel}`;
        localStorage.setItem(addressKey, fullAddr);
        setFullAddress(fullAddr);
      }

      setError(false);
    } catch {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [addressId, streetName, addressLabel]);

  const forceFetchSchedule = useCallback(() => {
    localStorage.removeItem(scheduleKey);
    fetchSchedule();
  }, [fetchSchedule]);

  useEffect(() => {
    fetchStreets();
  }, [fetchStreets]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule, addressId, streetName, addressLabel]);

  const resetState = useCallback(() => {
    setCityId(null);
    setStreetId(null);
    setAddressId(null);
    setAddresses(null);
    setSchedule([]);
    localStorage.removeItem(scheduleKey);
    localStorage.removeItem(addressKey);
    localStorage.removeItem(addressIdKey);
  }, []);

  const resetModalState = useCallback(() => {
    setCityId(null);
    setStreetId(null);
    setAddressId(null);
    setAddresses(null);
  }, []);

  return {
    isLoading,
    fullAddress,
    schedule,
    error,
    cityId,
    formattedCities,
    formattedStreets,
    addresses,
    formattedNumbers,
    setAddressId,
    setAddressLabel,
    setCityId,
    setStreetId,
    setStreetName,
    setAddresses,
    resetState,
    resetModalState,
    forceFetchSchedule
  };
}
